import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Button, Space, Tag, Input, Row, Col, Descriptions, App } from 'antd';
import { SearchOutlined, EyeOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { formatDateBeijing } from '../utils/datetime';

const { Title, Text, Paragraph } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const PolicyLaws = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadPolicies = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize,
        search: searchText,
        category: categoryFilter,
        type: typeFilter,
        source: sourceFilter,
      });

      const response = await fetch(`${API_BASE_URL}/industry/policies?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setPolicies(data.data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: data.total,
        });
      } else {
        message.error('加载政策法规数据失败');
      }
    } catch (error) {
      console.error('加载政策法规数据失败:', error);
      message.error('加载政策法规数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, [searchText, categoryFilter, typeFilter, sourceFilter]);

  const getCategoryTag = (category) => {
    const colors = {
      '国家': 'red',
      '地方': 'blue',
      '专项': 'green',
    };
    return <Tag color={colors[category] || 'default'}>{category}</Tag>;
  };

  const getTypeTag = (type) => {
    const colors = {
      '政策': 'orange',
      '法规': 'purple',
      '标准解读': 'cyan',
    };
    return <Tag color={colors[type] || 'default'}>{type}</Tag>;
  };

  const columns = [
    {
      title: '政策标题',
      dataIndex: 'title',
      key: 'title',
      width: '30%',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: '10%',
      render: (category) => getCategoryTag(category),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: '10%',
      render: (type) => getTypeTag(type),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: '15%',
    },
    {
      title: '发布日期',
      dataIndex: 'publish_date',
      key: 'publish_date',
      width: '13%',
      render: (date) => formatDateBeijing(date) || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: '18%',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} size="small">
            预览
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} size="small">
            下载
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    loadPolicies(pagination.current, pagination.pageSize);
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>政策法规</Title>
        <Text type="secondary">国家、地方及专项政策法规库</Text>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="搜索政策标题或内容"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="按分类筛选（国家/地方/专项）"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="按类型筛选（政策/法规/标准解读）"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="按来源筛选"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={policies}
          loading={loading}
          rowKey="id"
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '16px' }}>
                <Descriptions bordered size="small">
                  <Descriptions.Item label="政策标题" span={3}>
                    {record.title}
                  </Descriptions.Item>
                  <Descriptions.Item label="分类" span={1}>
                    {getCategoryTag(record.category)}
                  </Descriptions.Item>
                  <Descriptions.Item label="类型" span={1}>
                    {getTypeTag(record.type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="来源" span={1}>
                    {record.source}
                  </Descriptions.Item>
                  <Descriptions.Item label="发布日期" span={1}>
                    {formatDateBeijing(record.publish_date) || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="实施日期" span={1}>
                    {formatDateBeijing(record.implementation_date) || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="文件大小" span={2}>
                    {record.file_size ? `${(record.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="内容摘要" span={3}>
                    <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}>
                      {record.content || '暂无内容摘要'}
                    </Paragraph>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            ),
            expandIcon: ({ expanded, onExpand, record }) => (
              <Button
                type="link"
                onClick={(e) => onExpand(record, e)}
                style={{ padding: 0 }}
              >
                {expanded ? '收起' : '查看详情'}
              </Button>
            ),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条政策法规`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default PolicyLaws;
